import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import * as seed from "../data/mockData";
import { initials, timeToMinutes } from "../utils/format";

const ClinicDataContext = createContext(null);

let idCounter = 1000;
const nextId = (prefix) => `${prefix}${++idCounter}`;

// Fills in the dentist fields older records may not have yet (schedule, profile, online booking, leave).
const withDentistDefaults = (d) => ({
  active: true,
  online: true,
  start: seed.CLINIC.openTime,
  end: seed.CLINIC.closeTime,
  breakStart: "",
  breakEnd: "",
  qualifications: "",
  experienceYears: "",
  languages: [],
  bio: "",
  fee: "",
  photoUrl: "",
  treatmentIds: [],
  leave: [],
  focus: [],
  ...d,
});

// Appointments that still need a dentist: today onwards and not finished, cancelled or missed.
const isOpenAppointment = (appt, today) =>
  appt.date >= today && !["cancelled", "no-show", "completed"].includes(appt.status);

export const leaveOn = (dentist, date) =>
  (dentist?.leave || []).find((l) => date >= l.from && date <= l.to) || null;

export const ClinicDataProvider = ({ children }) => {
  const [patients, setPatients] = useState(seed.patients);
  const [appointments, setAppointments] = useState(seed.appointments);
  const [invoices, setInvoices] = useState(seed.invoices);
  const [treatments, setTreatments] = useState(seed.treatments);
  const [products, setProducts] = useState(seed.products);
  const [dentists, setDentists] = useState(() => seed.dentists.map(withDentistDefaults));
  const activeDentists = useMemo(() => dentists.filter((d) => d.active), [dentists]);

  const treatmentById = useMemo(
    () => Object.fromEntries(treatments.map((t) => [t.id, t])),
    [treatments]
  );
  const patientById = useMemo(
    () => Object.fromEntries(patients.map((p) => [p.id, p])),
    [patients]
  );
  const productById = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p])),
    [products]
  );
  const dentistById = useMemo(
    () => Object.fromEntries(dentists.map((d) => [d.id, d])),
    [dentists]
  );

  const itemPrice = useCallback(
    (item) => item.price ?? treatmentById[item.treatmentId]?.price ?? productById[item.productId]?.price ?? 0,
    [treatmentById, productById]
  );

  const itemName = useCallback(
    (item) => item.name || treatmentById[item.treatmentId]?.name || productById[item.productId]?.name || "Item",
    [treatmentById, productById]
  );

  // Amount the patient is responsible for: items, less discount and any panel coverage.
  const invoiceTotal = useCallback(
    (invoice) => {
      const gross = invoice.items.reduce((sum, item) => sum + itemPrice(item) * item.qty, 0);
      return Math.max(gross - (invoice.discount || 0) - (invoice.panel?.amount || 0), 0);
    },
    [itemPrice]
  );

  const invoiceStatus = useCallback(
    (invoice) => {
      const total = invoiceTotal(invoice);
      if (invoice.paid >= total) return "paid";
      if (invoice.dueDate < seed.TODAY) return "overdue";
      if (invoice.paid > 0) return "partial";
      return "unpaid";
    },
    [invoiceTotal]
  );

  const patientBalance = useCallback(
    (patientId) =>
      invoices
        .filter((inv) => inv.patientId === patientId)
        .reduce((sum, inv) => sum + Math.max(invoiceTotal(inv) - inv.paid, 0), 0),
    [invoices, invoiceTotal]
  );

  // An appointment can include several treatments; these helpers read them as one visit.
  const apptTreatments = useCallback(
    (appt) => (appt.treatmentIds || []).map((id) => treatmentById[id]).filter(Boolean),
    [treatmentById]
  );

  const treatmentsDuration = useCallback(
    (ids) => (ids || []).reduce((sum, id) => sum + (treatmentById[id]?.duration ?? 0), 0) || 30,
    [treatmentById]
  );

  const apptDuration = useCallback((appt) => treatmentsDuration(appt.treatmentIds), [treatmentsDuration]);

  const apptLabel = useCallback(
    (appt) => apptTreatments(appt).map((t) => t.name).join(", ") || "Appointment",
    [apptTreatments]
  );

  const apptPrice = useCallback(
    (appt) => apptTreatments(appt).reduce((sum, t) => sum + t.price, 0),
    [apptTreatments]
  );

  // Returns an appointment for the same dentist that overlaps the given slot, if any.
  const findClash = useCallback(
    ({ dentistId, date, start, treatmentIds, ignoreId }) => {
      const startMin = timeToMinutes(start);
      const endMin = startMin + treatmentsDuration(treatmentIds);
      return appointments.find((appt) => {
        if (appt.id === ignoreId || appt.dentistId !== dentistId || appt.date !== date) return false;
        if (appt.status === "cancelled" || appt.status === "no-show") return false;
        const s = timeToMinutes(appt.start);
        const e = s + apptDuration(appt);
        return startMin < e && endMin > s;
      });
    },
    [appointments, treatmentsDuration, apptDuration]
  );

  const addAppointment = useCallback((data) => {
    const appt = { id: nextId("a"), status: "scheduled", notes: "", ...data };
    setAppointments((list) => [...list, appt]);
    return appt;
  }, []);

  const updateAppointment = useCallback((id, changes) => {
    setAppointments((list) => list.map((appt) => (appt.id === id ? { ...appt, ...changes } : appt)));
  }, []);

  const addPatient = useCallback((data) => {
    const patient = {
      id: nextId("p"),
      alerts: [],
      lastVisit: null,
      registered: seed.TODAY,
      ...data,
    };
    setPatients((list) => [patient, ...list]);
    return patient;
  }, []);


  const recordPayment = useCallback((invoiceId, amount, method) => {
    setInvoices((list) =>
      list.map((inv) =>
        inv.id === invoiceId
          ? {
              ...inv,
              paid: inv.paid + amount,
              method,
              payments: [...(inv.payments || []), { method, amount, at: new Date().toISOString() }],
            }
          : inv
      )
    );
  }, []);

  // Creates a paid (or part-paid) invoice from the checkout counter.
  const createSale = useCallback(
    ({ patientId, appointmentId, items, discount = 0, panel = null, payments = [] }) => {
      const lastNumber = Math.max(...invoices.map((inv) => Number(inv.id.split("-").pop()) || 0), 0);
      const id = `INV-${seed.TODAY.slice(0, 4)}-${String(lastNumber + 1).padStart(4, "0")}`;
      const paid = payments.reduce((sum, p) => sum + p.amount, 0);
      const invoice = {
        id,
        patientId,
        appointmentId,
        date: seed.TODAY,
        dueDate: seed.TODAY,
        items,
        discount,
        panel,
        payments,
        paid,
        method: [...new Set(payments.map((p) => p.method))].join(" + "),
        createdAt: new Date().toISOString(),
      };
      setInvoices((list) => [invoice, ...list]);
      if (appointmentId) {
        setAppointments((list) => list.map((a) => (a.id === appointmentId ? { ...a, checkedOut: id } : a)));
      }
      if (patientId) {
        setPatients((list) => list.map((p) => (p.id === patientId ? { ...p, lastVisit: seed.TODAY } : p)));
      }
      const sold = items.filter((i) => i.productId);
      if (sold.length) {
        setProducts((list) =>
          list.map((prod) => {
            const line = sold.find((i) => i.productId === prod.id);
            return line ? { ...prod, stock: Math.max(prod.stock - line.qty, 0) } : prod;
          })
        );
      }
      return invoice;
    },
    [invoices]
  );

  // ----- Dentists -----
  const saveDentist = useCallback((data) => {
    const record = { ...data, initials: initials(data.name) };
    if (data.id) {
      setDentists((list) => list.map((d) => (d.id === data.id ? { ...d, ...record } : d)));
      return record;
    }
    const created = withDentistDefaults({ ...record, id: nextId("d") });
    setDentists((list) => [...list, created]);
    return created;
  }, []);

  const setDentistActive = useCallback((id, active) => {
    setDentists((list) => list.map((d) => (d.id === id ? { ...d, active } : d)));
  }, []);

  const addLeave = useCallback((dentistId, leave) => {
    setDentists((list) =>
      list.map((d) =>
        d.id === dentistId
          ? { ...d, leave: [...d.leave, { ...leave, id: nextId("l") }].sort((a, b) => a.from.localeCompare(b.from)) }
          : d
      )
    );
  }, []);

  const removeLeave = useCallback((dentistId, leaveId) => {
    setDentists((list) => list.map((d) => (d.id === dentistId ? { ...d, leave: d.leave.filter((l) => l.id !== leaveId) } : d)));
  }, []);

  const openAppointmentsFor = useCallback(
    (dentistId, from = seed.TODAY, to = "9999-12-31") =>
      appointments
        .filter((a) => a.dentistId === dentistId && isOpenAppointment(a, seed.TODAY) && a.date >= from && a.date <= to)
        .sort((x, y) => (x.date + x.start).localeCompare(y.date + y.start)),
    [appointments]
  );

  // Moves a dentist's remaining appointments to a colleague (used before deactivating).
  const reassignAppointments = useCallback((fromId, toId) => {
    setAppointments((list) =>
      list.map((a) => (a.dentistId === fromId && isOpenAppointment(a, seed.TODAY) ? { ...a, dentistId: toId } : a))
    );
  }, []);

  const saveTreatment = useCallback((data) => {
    if (data.id) {
      setTreatments((list) => list.map((t) => (t.id === data.id ? { ...t, ...data } : t)));
    } else {
      setTreatments((list) => [...list, { ...data, id: nextId("t") }]);
    }
  }, []);

  const value = {
    today: seed.TODAY,
    clinic: seed.CLINIC,
    patients,
    appointments,
    invoices,
    treatments,
    products,
    dentists,
    activeDentists,
    treatmentById,
    patientById,
    dentistById,
    productById,
    itemPrice,
    itemName,
    invoiceTotal,
    invoiceStatus,
    patientBalance,
    apptTreatments,
    apptDuration,
    apptLabel,
    apptPrice,
    treatmentsDuration,
    findClash,
    addAppointment,
    updateAppointment,
    addPatient,
    recordPayment,
    createSale,
    saveTreatment,
    saveDentist,
    setDentistActive,
    addLeave,
    removeLeave,
    openAppointmentsFor,
    reassignAppointments,
  };

  return <ClinicDataContext.Provider value={value}>{children}</ClinicDataContext.Provider>;
};

export const useClinicData = () => {
  const ctx = useContext(ClinicDataContext);
  if (!ctx) throw new Error("useClinicData must be used inside ClinicDataProvider");
  return ctx;
};
